import _ from 'lodash'
import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'

import Config from '@glass-designer/config'
import { SegmentTypes } from '@/store/mathTypes'
import { UserError } from '@/utils/errors'
import MathsUtils from '@/utils/mathsUtils'


/**
 * Clockwise Notes
 * ------------------
 * For some reason THREE.js flips concept of clockwise in ellipses (used for arcs)
 * Probably to match maths concept of ellipses, but it looks incorrect in our coordinate systems used
 * We use Y=0 is top-left of screen in ortho view which may cause differences (maths diagrams has Y=0 bottom-left)
 * So we just flip it for interaction with THREE.js functions, and maintain clockwise matches screen everywhere else
 */


/*
 * GlassSegment
 * Represents a segment of the glass piece. It contains all the cutting details as well as edge details
 * Segments contains a start and end position, so it's up to the GlassPiece to ensure all end/start points in
 * the list of segments are linked.
 * All corner cuts are applied to the *start* position of a segment.
 * Corner cuts to the end of the segment must be applied to the start of the next segment
 */
export default class GlassSegment {
  constructor(type, options = {}) {
    this.id = uuidv4()
    this.type = type

    // Misc flags
    this.isSegmentSelected = false
    this.isSegmentStartSelected = false

    // If converting corner radiuses to seperate arcs, flag if the arc was a corner
    this.isArcFromCornerRadius = false

    // If the user locks in the length/radius/angle of the segment to be a fixed value, it cannot be adjusted automatically
    // Segments get adjusted when neighbouring segments are changed via dimensions (eg, whole edges moved) or cutouts are added
    // The segments are fixed via editing dimensions / cutouts. They can be cleared by the user too
    this.isUserFixed = false
    this.isAngleUserFixed = false

    // Edgework settings
    // Updated during shape updates. If a segment is part of an interior cut away from the convex hull, edgework is not available
    this.isEdgeworkAvailable = true
    this.orderEditorEdgeworkConfigId = 0
    this.edgeworkSettingValue = 0

    // Edge/Corner/Radius Cutout Settings
    this.isCutoutPolished = !!options.isCutoutPolished
    this.isStartCornerPolished = !!options.isStartCornerPolished


    // Corner radius (in mm) for the start & end corners
    // The prev/next segments should have matching corner radius settings
    this.startCornerRadius = options.startCornerRadius || 0
    this.endCornerRadius = options.endCornerRadius || 0

    // Store the delta vectors for the corner radiuses
    // Based on previous/next segments in the list
    // Stored here so functions don't always need to reference the entire list, especially for open lists (eg, cutouts)
    // Must be always updated if the segment list is updated
    this.startCornerRadiusDelta = new THREE.Vector2()
    this.endCornerRadiusDelta = new THREE.Vector2()

    this.startPosition = new THREE.Vector2()
    this.endPosition = new THREE.Vector2()

    if(options.startPosition)
      this.startPosition.set(options.startPosition.x, options.startPosition.y)
    if(options.endPosition)
      this.endPosition.set(options.endPosition.x, options.endPosition.y)

    if(options.startCornerRadiusDelta)
      this.startCornerRadiusDelta.set(options.startCornerRadiusDelta.x, options.startCornerRadiusDelta.y)
    if(options.endCornerRadiusDelta)
      this.endCornerRadiusDelta.set(options.endCornerRadiusDelta.x, options.endCornerRadiusDelta.y)

    // Arc details if type == SegmentTypes.arc
    // Angles in degrees
    this.arcEllipseCurve = null
    this.arcCenterPosition = new THREE.Vector2()
    this.arcRadius = options.arcRadius || 50
    this.arcStartAngle = options.arcStartAngle || 0
    this.arcEndAngle = options.arcEndAngle || 0
    this.arcClockwise = options.arcClockwise !== undefined ? !!options.arcClockwise : true
    
    // Edit type. Used to store how the arc was editing (in the editor)
    this.arcEditType = options.arcEditType

    if(options.arcCenterPosition)
      this.arcCenterPosition.set(options.arcCenterPosition.x, options.arcCenterPosition.y)

    if(this.type == SegmentTypes.arc)
      this.arcUpdated()
  }

  // Get the length of this segment
  getLength() {
    if(this.type == SegmentTypes.line)
      return this.startPosition.distanceTo(this.endPosition)

    else if(this.type == SegmentTypes.arc) {
      var angleDist = MathsUtils.calculateAngleDistanceDegrees(this.arcStartAngle, this.arcEndAngle, this.arcClockwise)
      let totalCircumferance = 2 * Math.PI * this.arcRadius
      if(angleDist <= 0)
        return 0

      return totalCircumferance * (Math.abs(angleDist) / 360.0)
    }

    return 0
  }


  /**
   * Calculate the direction of the segment
   * Start -> Endpoint is used for both lines & arcs
   * Arc direction is just an approximation & should not really use this function
   * 
   * Returns a normalized Vector2 object
   */
  getDirection() {
    var dir = new THREE.Vector2()
    dir.subVectors(this.endPosition, this.startPosition)
    dir.normalize()
    return dir
  }


  /**
   * Calculate the outwards direction of the segment
   * Start -> Endpoint is used for both lines & arcs
   * Arc direction is just an approximation & should not really use this function
   * 
   * Returns a normalized Vector2 object
   */
  getOutDirection() {
    let dir = this.getDirection()

    // Rotate by 90deg in 2D
    var outDir = new THREE.Vector2(dir.y, -dir.x)
    return outDir
  }


  /**
   * Calculate the angle of the 2D segment direction in radians
   * Start -> Endpoint is used for both lines & arcs
   * Arc direction is just an approximation & should not really use this function
   */
  getAngle() {
    var dir = this.getDirection()
    return dir.angle()
  }


  /**
   * Calculates the midpoint of the segment
   */
  getMidpoint() {
    if(this.type == SegmentTypes.line)
      return new THREE.Vector2((this.endPosition.x + this.startPosition.x) / 2, (this.endPosition.y + this.startPosition.y) / 2)

    else if(this.type == SegmentTypes.arc) {
      var midpoint = new THREE.Vector2()

      if(this.arcEllipseCurve)
        this.arcEllipseCurve.getPoint(0.5, midpoint)

      return midpoint
    }
  }


  /**
   * Get a point given a fraction along the path of the segment between 0 and 1
   * Note: Ignores corner radiuses
   * Note: Fraction is clamped to within 0->1
   * Returns a 2D point for the fraction
   */
  getPoint(fraction) {
    var point = new THREE.Vector2()
    fraction = Math.min(Math.max(fraction, 0.0), 1.0)

    if(this.type == SegmentTypes.line) {
      point.x = this.startPosition.x + (this.endPosition.x - this.startPosition.x) * fraction
      point.y = this.startPosition.y + (this.endPosition.y - this.startPosition.y) * fraction
    }
    else if(this.type == SegmentTypes.arc) {

      if(this.arcEllipseCurve)
        this.arcEllipseCurve.getPoint(fraction, point)
    }

    return point
  }



  /**
   * Calculate the bounds of the glass segment
   * Returns a Box2 object for the 2D min/max of the segment
   */
  calculateBounds() {
    var bounds = new THREE.Box2()

    if(this.type == SegmentTypes.line) {
      bounds.set(this.startPosition, this.startPosition)
      bounds.expandByPoint(this.endPosition)
    }
    else if(this.type == SegmentTypes.arc) {
      if(this.arcEllipseCurve) {
        var subdivisions = Math.max(Config.Settings.arcSubdivisions, Math.ceil(this.getLength() / 15))

        var points = this.arcEllipseCurve.getPoints(subdivisions)
        points.forEach(shapePnt => {
          bounds.expandByPoint(shapePnt)
        })
        bounds.expandByPoint(this.getMidpoint())
      } else {
        bounds.set(this.startPosition, this.startPosition)
        bounds.expandByPoint(this.endPosition)
      }
    }

    return bounds
  }


  /**
   * Returns a cloned copy of the glass segment
   */
  clone() {
    var segCopy = new GlassSegment(this.type, {
      startCornerRadius: this.startCornerRadius,
      endCornerRadius: this.endCornerRadius,
      arcRadius: this.arcRadius,
      arcStartAngle: this.arcStartAngle,
      arcEndAngle: this.arcEndAngle,
      arcClockwise: this.arcClockwise,
      arcEditType: this.arcEditType,
      startCornerRadiusDelta: this.startCornerRadiusDelta,
      endCornerRadiusDelta: this.endCornerRadiusDelta
    })

    segCopy.isUserFixed = this.isUserFixed
    segCopy.isAngleUserFixed = this.isAngleUserFixed
    segCopy.isEdgeworkAvailable = this.isEdgeworkAvailable
    segCopy.orderEditorEdgeworkConfigId = this.orderEditorEdgeworkConfigId
    segCopy.edgeworkSettingValue = this.edgeworkSettingValue
    segCopy.isCutoutPolished = this.isCutoutPolished
    segCopy.isStartCornerPolished = this.isStartCornerPolished
    segCopy.isArcFromCornerRadius = this.isArcFromCornerRadius

    segCopy.startPosition = this.startPosition.clone()
    segCopy.endPosition = this.endPosition.clone()
    segCopy.arcCenterPosition = this.arcCenterPosition.clone()
    segCopy.arcUpdated()

    return segCopy
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {
    var objData = {
      id: this.id,
      type: this.type,
      isUserFixed: this.isUserFixed,
      isAngleUserFixed: this.isAngleUserFixed,
      isEdgeworkAvailable: this.isEdgeworkAvailable,
      orderEditorEdgeworkConfigId: this.orderEditorEdgeworkConfigId,
      edgeworkSettingValue: this.edgeworkSettingValue,
      isCutoutPolished: this.isCutoutPolished,
      isStartCornerPolished: this.isStartCornerPolished,
      startCornerRadius: this.startCornerRadius,
      endCornerRadius: this.endCornerRadius,
      startCornerRadiusDelta: MathsUtils.serializeVector2(this.startCornerRadiusDelta),
      endCornerRadiusDelta: MathsUtils.serializeVector2(this.endCornerRadiusDelta),
      startPosition: MathsUtils.serializeVector2(this.startPosition),
      endPosition: MathsUtils.serializeVector2(this.endPosition),
      arcCenterPosition: MathsUtils.serializeVector2(this.arcCenterPosition),
      arcRadius: this.arcRadius,
      arcStartAngle: this.arcStartAngle,
      arcEndAngle: this.arcEndAngle,
      arcClockwise: this.arcClockwise,
      arcEditType: this.arcEditType,
      isArcFromCornerRadius: this.isArcFromCornerRadius
    }

    return objData
  }
  deserialize(object) {
    this.id = object.id || this.id
    this.type = object.type
    this.isUserFixed = !!object.isUserFixed
    this.isAngleUserFixed = !!object.isAngleUserFixed
    this.isEdgeworkAvailable = !!object.isEdgeworkAvailable,
    this.orderEditorEdgeworkConfigId = parseInt(object.orderEditorEdgeworkConfigId || 0)
    this.edgeworkSettingValue = parseFloat(object.edgeworkSettingValue || 0)

    this.isCutoutPolished = !!object.isCutoutPolished
    this.isStartCornerPolished = !!object.isStartCornerPolished

    this.startCornerRadius = parseFloat(object.startCornerRadius || 0)
    this.endCornerRadius = parseFloat(object.endCornerRadius || 0)
    
    this.startPosition = MathsUtils.deserializeVector2(object.startPosition)
    this.endPosition = MathsUtils.deserializeVector2(object.endPosition)

    this.startCornerRadiusDelta = MathsUtils.deserializeVector2(object.startCornerRadiusDelta)
    this.endCornerRadiusDelta = MathsUtils.deserializeVector2(object.endCornerRadiusDelta)

    this.arcCenterPosition = MathsUtils.deserializeVector2(object.arcCenterPosition)
    this.arcRadius = parseFloat(object.arcRadius || 0)
    this.arcStartAngle = parseFloat(object.arcStartAngle || 0)
    this.arcEndAngle = parseFloat(object.arcEndAngle || 0)
    this.arcClockwise = !!object.arcClockwise
    this.arcEditType = object.arcEditType
    this.isArcFromCornerRadius = !!object.isArcFromCornerRadius

    this.arcUpdated()
  }


  /**
   * Check if this segment equals another segment based on position/arc details
   */
  equalsPositionData(otherSegment, threshold = 0.001) {
    if(this.type != otherSegment.type)
      return false
    if(!MathsUtils.check2DPointsEqual(this.startPosition, otherSegment.startPosition, threshold))
      return false
    if(!MathsUtils.check2DPointsEqual(this.endPosition, otherSegment.endPosition, threshold))
      return false
    
    if(this.type == SegmentTypes.arc) {
      if(!MathsUtils.check2DPointsEqual(this.arcCenterPosition, otherSegment.arcCenterPosition, threshold))
        return false
      if(Math.abs(this.arcRadius - otherSegment.arcRadius) > threshold)
        return false
      if(Math.abs(this.arcStartAngle - otherSegment.arcStartAngle) > threshold)
        return false
      if(Math.abs(this.arcEndAngle - otherSegment.arcEndAngle) > threshold)
        return false
      if(this.arcClockwise != otherSegment.arcClockwise)
        return false
    }

    return true

  }

  clearSelection() {
    this.isSegmentSelected = false
    this.isSegmentStartSelected = false
  }
  setSegmentSelected(selected) {
    this.isSegmentSelected = selected
  }
  getSegmentSelected() {
    return this.isSegmentSelected
  }
  setSegmentStartSelected(selected) {
    this.isSegmentStartSelected = selected
  }
  getSegmentStartSelected() {
    return this.isSegmentStartSelected
  }

  /**
   * Project a point onto the segment & return the closest point on the segment to the given point
   * Returns a Vector2 object with the closest point
   */
  projectPointOnSegment(point, clampEnds = true) {
    if(this.type == SegmentTypes.arc) {
      let pointAngle = MathsUtils.calculateArcPointAngle(this.arcCenterPosition, point)

      let curveFraction = MathsUtils.mapAngleDegreesValue(pointAngle, this.arcStartAngle, this.arcEndAngle, this.arcClockwise)
      if(curveFraction == 0.0) {
        // Get closest point to the ends of the arc if the angle is not within the arc
        if(point.distanceToSquared(this.startPosition) < point.distanceToSquared(this.endPosition))
          return this.startPosition
        
        return this.endPosition
      }

      if(this.arcEllipseCurve)
        return this.arcEllipseCurve.getPoint(MathsUtils.clampValue(curveFraction, 0.0, 1.0), new THREE.Vector2())

      return this.startPosition
    }

    if(this.type == SegmentTypes.line)
      return MathsUtils.getClosest2DPointOnLineSegment(this.startPosition, this.endPosition, new THREE.Vector2(point.x, point.y), clampEnds)
  }


  /**
   * Calculate the intersection between a line (given a start & end point) and this glass segment
   * Returns an array of Vector2 objects for the intersection points
   * Returns an empty array if no intersection occured
   */
  calculateLineIntersection(startPoint, endPoint) {

    if(this.type == SegmentTypes.arc) {
      let results = MathsUtils.checkSegmentArcIntersection(startPoint, endPoint, this.arcCenterPosition, this.arcRadius, this.arcStartAngle, this.arcEndAngle, this.arcClockwise)
      return results
    }

    if(this.type == SegmentTypes.line) {
      var intersectionPoint = new THREE.Vector2()
      if(MathsUtils.checkSegmentSegmentIntersection(startPoint, endPoint, this.startPosition, this.endPosition, intersectionPoint)) {
        return [intersectionPoint]
      }

      return []
    }
  }


  /**
   * Calculate the intersection between an arc (given arc details) and this glass segment
   * Returns an array of Vector2 objects for the intersection points
   * Returns an empty array if no intersection occured
   */
  calculateArcIntersection(centerPoint, radius, startAngle, endAngle, clockwise) {

    if(this.type == SegmentTypes.arc) {
      let results = MathsUtils.checkArcArcIntersection(
        this.arcCenterPosition, this.arcRadius, this.arcStartAngle, this.arcEndAngle, this.arcClockwise,
        centerPoint, radius, startAngle, endAngle, clockwise
      )
      
      return results
    }

    if(this.type == SegmentTypes.line) {
      let results = MathsUtils.checkSegmentArcIntersection(
        this.startPosition,
        this.endPosition,
        centerPoint,
        radius,
        startAngle,
        endAngle,
        clockwise
      )
      
      return results
    }
  }


  /**
   * Calculate an intersection between this glass segment and another glass segment
   * Returns an array of Vector2 objects for the intersection points
   * Returns an empty array if no intersection occured
   */
  calculateGlassSegmentIntersection(otherSegment, endPointThreshold = 0) {
    // Other segment is an arc
    if(otherSegment.type == SegmentTypes.arc) {
      let results = this.calculateArcIntersection(
        otherSegment.arcCenterPosition,
        otherSegment.arcRadius,
        otherSegment.arcStartAngle,
        otherSegment.arcEndAngle,
        otherSegment.arcClockwise
      )

      // Check endpoint threshold
      if(endPointThreshold > 0 && results.length > 0) {
        if(results[0].distanceTo(otherSegment.startPosition) <= endPointThreshold || results[0].distanceTo(otherSegment.endPosition) <= endPointThreshold)
          return []
        if(results[0].distanceTo(this.startPosition) <= endPointThreshold || results[0].distanceTo(this.endPosition) <= endPointThreshold)
          return []
        if(results.length > 1) {
          if(results[1].distanceTo(otherSegment.startPosition) <= endPointThreshold || results[1].distanceTo(otherSegment.endPosition) <= endPointThreshold)
            return []
          if(results[1].distanceTo(this.startPosition) <= endPointThreshold || results[1].distanceTo(this.endPosition) <= endPointThreshold)
            return []
        }
      }

      return results
    }

    // Other segment is a line
    if(otherSegment.type == SegmentTypes.line) {
      let results = this.calculateLineIntersection(otherSegment.startPosition, otherSegment.endPosition, endPointThreshold)

      if(endPointThreshold > 0 && results.length > 0) {
        if(results[0].distanceTo(otherSegment.startPosition) <= endPointThreshold || results[0].distanceTo(otherSegment.endPosition) <= endPointThreshold)
          return []
        if(results[0].distanceTo(this.startPosition) <= endPointThreshold || results[0].distanceTo(this.endPosition) <= endPointThreshold)
          return []
        if(results.length > 1) {
          if(results[1].distanceTo(otherSegment.startPosition) <= endPointThreshold || results[1].distanceTo(otherSegment.endPosition) <= endPointThreshold)
            return []
          if(results[1].distanceTo(this.startPosition) <= endPointThreshold || results[1].distanceTo(this.endPosition) <= endPointThreshold)
            return []
        }
      }

      return results
    }

    return []
  }


  /**
   * Calculates if this segment intersects with a box
   * Returns an array of Vector2 objects for the intersection points
   * Returns an empty array if no intersection occured
   */
  calculateBox2Intersection(box) {
    let intList = []
    let boxTL = new THREE.Vector2(box.min.x, box.min.y)
    let boxTR = new THREE.Vector2(box.max.x, box.min.y)
    let boxBR = new THREE.Vector2(box.max.x, box.max.y)
    let boxBL = new THREE.Vector2(box.min.x, box.max.y)

    intList = intList.concat(this.calculateLineIntersection(boxTL, boxTR))
    intList = intList.concat(this.calculateLineIntersection(boxTR, boxBR))
    intList = intList.concat(this.calculateLineIntersection(boxBR, boxBL))
    intList = intList.concat(this.calculateLineIntersection(boxBL, boxTL))

    return intList
  }


  /**
   * Calculate the projected distance from the out direction of the segment to a point
   * Calculated in 2D coordinates
   * Returns a minus distance if the point in on the other side of the segment
   */
  calculateProjectDistanceToSegmentPlane(point) {
    let normal = this.getOutDirection()

    return normal.dot(point) - this.startPosition.dot(normal)
  }


  /**
   * Calculate the arc height for the arc
   * The distance between midpoint of the start/end positions and the apex of the arc
   */
  calculateArcHeight() {
    var dir = new THREE.Vector2()
    dir.subVectors(this.endPosition, this.startPosition)
    dir.normalize()

    let outDir = new THREE.Vector2(dir.y, -dir.x)
    let midpoint = new THREE.Vector2((this.endPosition.x + this.startPosition.x) / 2, (this.endPosition.y + this.startPosition.y) / 2)

    let apex = this.getMidpoint()

    return Math.abs(outDir.dot(apex) - midpoint.dot(outDir))
  }


  /**
   * Update the segment arc by setting the arc radius
   * Readjusts the midpoint to match the start/end positions with the updated arc height
   */
  updateArcByRadius(radius) {
    if(radius < 0)
      return

    let midpoint = new THREE.Vector2((this.startPosition.x + this.endPosition.x) / 2.0, (this.startPosition.y + this.endPosition.y) / 2.0)

    var dir = new THREE.Vector2()
    dir.subVectors(this.endPosition, this.startPosition)
    dir.normalize()
    let outDir = new THREE.Vector2(dir.y, -dir.x)

    let segLength = this.startPosition.distanceTo(this.endPosition)

    // Cannot be less than half the distance between points
    if(radius < Math.floor(segLength / 2.0))
      throw new UserError('Radius cannot be less than half the distance of the points')

    radius = Math.max(radius, segLength / 2.0)

    let circleSolutions = MathsUtils.calculateCircleInfoForPointsRadius(this.startPosition, this.endPosition, radius)
    if(circleSolutions.length == 0)
      throw new UserError('Cannot calculate arc for radius')

    this.arcRadius = radius
    this.arcCenterPosition.copy(circleSolutions[0].position)

    // If 2 solutions, get the center on the correct side of the start/end
    // Flipped for CCW
    if(circleSolutions.length == 2) {
      // Assumes the solutions are mirrored by calculateCircleInfoForPointsRadius() for either side of the points
      let sideB = MathsUtils.calculateDistanceToPlane2D(outDir, midpoint, circleSolutions[1].position)

      if((sideB < 0 && this.arcClockwise) || (sideB > 0 && !this.arcClockwise))
        this.arcCenterPosition.copy(circleSolutions[1].position)
    }

    this.arcStartAngle = MathsUtils.calculateArcPointAngle(this.arcCenterPosition, this.startPosition)
    this.arcEndAngle = MathsUtils.calculateArcPointAngle(this.arcCenterPosition, this.endPosition)

    this.arcUpdated()
  }


  /**
   * Update the segment arc by setting the arc height only
   * Readjusts the midpoint to match the start/end positions with the updated arc height
   */
  updateArcByHeight(height) {
    if(height < 0)
      return

    var dir = new THREE.Vector2()
    dir.subVectors(this.endPosition, this.startPosition)
    dir.normalize()

    var outDir = new THREE.Vector2(dir.y, -dir.x)
    let midpoint = new THREE.Vector2((this.endPosition.x + this.startPosition.x) / 2, (this.endPosition.y + this.startPosition.y) / 2)

    // Flip around for CCW
    if(!this.arcClockwise)
      outDir.negate()

    let apex = midpoint.clone().addScaledVector(outDir, height)

    let circleInfo = MathsUtils.calculateCircleInfoForPoints(this.startPosition, apex, this.endPosition)
    if(circleInfo == null)
      throw new UserError('Cannot calculate arc for height')

    this.arcRadius = circleInfo.radius
    this.arcCenterPosition.copy(circleInfo.position)

    this.arcStartAngle = MathsUtils.calculateArcPointAngle(this.arcCenterPosition, this.startPosition)
    this.arcEndAngle = MathsUtils.calculateArcPointAngle(this.arcCenterPosition, this.endPosition)

    this.arcUpdated()
  }


  /**
   * Update the arc curve object for an arc glass segment
   * Required whenever the glass details are changed
   * Precalculates the subdivided points for the arc
   */
  arcUpdated() {
    if(this.type == SegmentTypes.arc) {
      this.arcEllipseCurve = new THREE.EllipseCurve(
        this.arcCenterPosition.x,
        this.arcCenterPosition.y,
        this.arcRadius,
        this.arcRadius,
        MathsUtils.degreesToRadians(this.arcStartAngle),
        MathsUtils.degreesToRadians(this.arcEndAngle),
        !this.arcClockwise,      // NOTE: See notes on top for explanation
        0
      )

      // Set start/end positions to match the point on the arc
      this.arcEllipseCurve.getPoint(0, this.startPosition)
      this.arcEllipseCurve.getPoint(1, this.endPosition)
    }
  }


  /**
   * Convert a line segment to an arc
   * Automatically calculates default radius
   * Sets start/end angles based on current start/end position
   */
  convertToArc() {
    if(this.type == SegmentTypes.line) {
      let midpoint = new THREE.Vector2((this.startPosition.x + this.endPosition.x) / 2.0, (this.startPosition.y + this.endPosition.y) / 2.0)
      let segLength = this.startPosition.distanceTo(this.endPosition)
      let outDir = this.getOutDirection()

      this.type = SegmentTypes.arc
      
      // Calculate default radius & center position
      // Default clockwise to go outwards
      this.arcCenterPosition.set(midpoint.x, midpoint.y)
      this.arcClockwise = true

      this.arcRadius = segLength / 2.0

      let circleSolutions = MathsUtils.calculateCircleInfoForPointsRadius(this.startPosition, this.endPosition, this.arcRadius)
      if(circleSolutions.length > 0) {
        this.arcCenterPosition.copy(circleSolutions[0].position)

        // Use second solution where applicable
        if(circleSolutions.length == 2) {
          // Assumes the solutions are mirrored by calculateCircleInfoForPointsRadius() for either side of the points
          let sideB = MathsUtils.calculateDistanceToPlane2D(outDir, midpoint, circleSolutions[1].position)
    
          if((sideB < 0 && this.arcClockwise) || (sideB > 0 && !this.arcClockwise))
            this.arcCenterPosition.copy(circleSolutions[1].position)
        }
      }
      
      this.arcStartAngle = MathsUtils.calculateArcPointAngle(this.arcCenterPosition, this.startPosition)
      this.arcEndAngle = MathsUtils.calculateArcPointAngle(this.arcCenterPosition, this.endPosition)
      

      this.arcUpdated()
    }
  }


  /**
   * Calculate the angle distance of an arc
   * Returns angle distance in degrees
   */
  calculateArcAngleDistance() {
    if(this.type != SegmentTypes.arc)
      return 0

    return MathsUtils.calculateAngleDistanceDegrees(this.arcStartAngle, this.arcEndAngle, this.arcClockwise)
  }



  /**
   * Convert an arc glass segment to a straight line
   */
  convertToLine() {
    if(this.type == SegmentTypes.arc) {
      this.type = SegmentTypes.line
    }
  }


  /**
   * Calculate the fraction of a point along the glass segment
   * Returns a value between 0 -> 1 if the point is along the segment
   * Does not clamp if the point is not along the segment
   * 0 = the start of the segment path, 1 = the end of the segment path
   * Assumes the point is on or near the segment
   */
  calculatePointFractionOnSegment(point) {
    if(this.type == SegmentTypes.line) {
      let segLength = this.getLength()
      if(segLength <= 0)
        return 0

      return this.startPosition.distanceTo(point) / segLength
    }

    if(this.type == SegmentTypes.arc) {
      let angleDist = MathsUtils.calculateAngleDistanceDegrees(this.arcStartAngle, this.arcEndAngle, this.arcClockwise)
      if(angleDist <= 0)
        return 0
      let pointAngle = MathsUtils.calculateArcPointAngle(this.arcCenterPosition, point)

      return Math.abs(pointAngle - this.arcStartAngle) / angleDist
    }
  }


  /**
   * Move the segment by an offset vector
   */
  moveSegment(offset) {
    this.startPosition.add(offset)
    this.endPosition.add(offset)
    if(this.type == SegmentTypes.arc) {
      this.arcCenterPosition.add(offset)
      this.arcUpdated()
    }

  }


  toString() {
    var outputObj = {
      id: this.id,
      type: this.type,
      startPosition: `[${this.startPosition.x},${this.startPosition.y}]`,
      endPosition: `[${this.endPosition.x},${this.endPosition.y}]`
    }
    return JSON.stringify(outputObj, null, 2)
  }


  
}
